/**
 * The shape of a scenario while it is being edited, and the words that make a
 * projection defensible: what each lever claims, how far the assumption
 * reaches, and which moves are ours rather than the model's.
 */

import type {
  ProjectedMove,
  SaveScenarioInput,
  ScenarioLever,
  ScenarioProjection,
  ScenarioView,
} from '../../../types/issues';

export interface ScenarioDraft {
  /** null until it has been saved */
  id: string | null;
  name: string;
  description: string;
  assumptions: string;
  /** 0 → only the named actors move; 1 → allies follow almost fully */
  propagation: number;
  levers: ScenarioLever[];
}

export const BLANK_DRAFT: ScenarioDraft = {
  id: null,
  name: '',
  description: '',
  assumptions: '',
  propagation: 0.5,
  levers: [],
};

let counter = 0;
export function newId(prefix = 'lever'): string {
  counter += 1;
  const random =
    typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function'
      ? (crypto as any).randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix}-${counter}-${random}`;
}

export function newLever(): ScenarioLever {
  return {
    id: newId(),
    label: '',
    description: null,
    cost: null,
    enabled: true,
    effects: [],
    segmentEffects: [],
  };
}

export function draftFrom(scenario: ScenarioView | null): ScenarioDraft {
  if (!scenario) return { ...BLANK_DRAFT, levers: [] };
  return {
    id: scenario.id,
    name: scenario.name || '',
    description: scenario.description || '',
    assumptions: scenario.assumptions || '',
    propagation: typeof scenario.propagation === 'number' ? scenario.propagation : 0.5,
    levers: (scenario.levers || []).map((lever) => ({
      ...lever,
      effects: lever.effects || [],
      segmentEffects: lever.segmentEffects || [],
    })),
  };
}

export function toInput(draft: ScenarioDraft): SaveScenarioInput {
  return {
    name: draft.name.trim() || 'Untitled scenario',
    description: draft.description.trim() || null,
    assumptions: draft.assumptions.trim() || null,
    propagation: draft.propagation,
    levers: draft.levers,
  };
}

/** Levers that are switched on and actually claim to move someone */
export function activeLevers(draft: ScenarioDraft): ScenarioLever[] {
  return draft.levers.filter(
    (lever) => lever.enabled && ((lever.effects?.length || 0) > 0 || (lever.segmentEffects?.length || 0) > 0),
  );
}

/** Nothing to project yet — say so rather than asking the server */
export const isProjectable = (draft: ScenarioDraft): boolean => activeLevers(draft).length > 0;

export const signedDelta = (delta: number): string => {
  const value = Math.round((delta || 0) * 10) / 10;
  if (value === 0) return '0';
  return `${value > 0 ? '+' : '−'}${Math.abs(value)}`;
};

/** "2 actors · Community leaders +1" — what a lever claims, in one line */
export function leverSummary(lever: ScenarioLever): string {
  const parts: string[] = [];
  const named = lever.effects?.length || 0;
  if (named) parts.push(`${named} ${named === 1 ? 'actor' : 'actors'}`);
  (lever.segmentEffects || []).forEach((segment) => parts.push(`${segment.category} ${signedDelta(segment.delta)}`));
  return parts.length ? parts.join(' · ') : 'Moves nobody yet';
}

/** The assumption the whole projection rests on, in one line */
export function propagationWords(propagation: number): string {
  const p = Math.max(0, Math.min(1, propagation ?? 0));
  if (p <= 0.02) return 'Only the actors we name move — nobody follows them';
  if (p < 0.35) return 'Allies shift slightly when someone they are tied to moves';
  if (p < 0.7) return 'Allies follow about half of the move, fading with each step';
  return 'Allies follow almost fully — the most optimistic reading';
}

/** Direct effects first, then the largest knock-on moves */
export function sortMoves(moves: ProjectedMove[]): ProjectedMove[] {
  return [...moves].sort(
    (a, b) =>
      Number(b.direct) - Number(a.direct) ||
      Math.abs(b.delta) - Math.abs(a.delta) ||
      (b.issueInfluence || 0) - (a.issueInfluence || 0),
  );
}

export interface ProjectionSummary {
  moved: number;
  towards: number;
  away: number;
  gained: number;
  lost: number;
  indexDelta: number;
}

export function summarise(projection: ScenarioProjection | null): ProjectionSummary {
  if (!projection) return { moved: 0, towards: 0, away: 0, gained: 0, lost: 0, indexDelta: 0 };
  const moves = projection.moves || [];
  return {
    moved: moves.length,
    towards: moves.filter((m) => m.delta > 0).length,
    away: moves.filter((m) => m.delta < 0).length,
    gained: projection.flips?.gained?.length || 0,
    lost: projection.flips?.lost?.length || 0,
    indexDelta: (projection.after?.index ?? 0) - (projection.before?.index ?? 0),
  };
}

/** "3 move towards us, 1 away" — the projection in a sentence */
export function summaryLine(projection: ScenarioProjection | null): string {
  const s = summarise(projection);
  if (!s.moved) return 'Nobody moves under these assumptions';
  const parts = [`${s.moved} ${s.moved === 1 ? 'actor moves' : 'actors move'}`];
  if (s.towards) parts.push(`${s.towards} towards us`);
  if (s.away) parts.push(`${s.away} away`);
  return parts.join(' · ');
}

/** Two drafts are the same projection question when their inputs match */
export function draftSignature(draft: ScenarioDraft): string {
  return JSON.stringify({
    propagation: Math.round((draft.propagation ?? 0) * 100) / 100,
    levers: activeLevers(draft).map((lever) => ({
      id: lever.id,
      effects: (lever.effects || []).map((e) => [e.subjectType, e.subjectId, e.delta]),
      segments: (lever.segmentEffects || []).map((s) => [s.category, s.delta]),
    })),
  });
}

/** Has anything the user typed diverged from what is saved? */
export function isDirty(draft: ScenarioDraft, saved: ScenarioView | null): boolean {
  if (!saved) return draft.name.trim().length > 0 || draft.levers.length > 0;
  return JSON.stringify(toInput(draft)) !== JSON.stringify(toInput(draftFrom(saved)));
}
